<template>
  <canvas v-if="enabled" ref="canvasRef" class="custom-watermark" />
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick, computed } from 'vue'
import { ELocalStorageKey } from '/@/types'
import useAppStore from '/@/store/pinia/app'
import { getConfigKey } from '/@/api/insp-device'

const appStore = useAppStore()
const enabled = computed(() => appStore.isWatermarkEnabled)

const watermarkText = computed(() => {
  if (username) {
    return `${username}`
  }
  return ''
})

const canvasRef = ref<HTMLCanvasElement | null>(null)
const ctx = ref<CanvasRenderingContext2D | null>(null)

const username = localStorage.getItem(ELocalStorageKey.Username)

type CanvasFontWeight = 'normal' | 'bold' | 'bolder' | 'lighter' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900'

// 水印配置
const watermarkConfig = ref({
  fontSize: 16,
  color: 'rgba(100, 100, 100, 0.3)',
  rotate: -30, // 倾斜角度
  horizontalSpacing: 200,
  verticalSpacing: 150,
  opacity: 0.6,
  fontWeight: 'normal' as CanvasFontWeight
})

const debounce = (fn: Function, delay: number) => {
  let timer: number | undefined
  return (...args: any[]) => {
    clearTimeout(timer)
    timer = window.setTimeout(() => fn(...args), delay)
  }
}

// 从接口获取水印配置
const fetchWatermarkConfig = async () => {
  try {
    const res = await getConfigKey('system_watermark')
    if (res) {
      appStore.setWatermarkEnabled(res.msg === 'true')
    }
  } catch (error) {
    console.error('获取水印配置失败:', error)
    appStore.setWatermarkEnabled(false)
  }
}

// 初始化 Canvas
const initCanvas = () => {
  if (!canvasRef.value) return

  const canvas = canvasRef.value
  const dpr = window.devicePixelRatio || 1
  const rect = canvas.getBoundingClientRect()

  // 设置 Canvas 尺寸
  canvas.width = rect.width * dpr
  canvas.height = rect.height * dpr

  ctx.value = canvas.getContext('2d')
  if (ctx.value) {
    ctx.value.scale(dpr, dpr)
    ctx.value.imageSmoothingEnabled = true
  }
}

// 绘制水印
const drawWatermark = () => {
  if (!ctx.value || !canvasRef.value || !watermarkText.value) return

  const canvas = canvasRef.value
  const { width, height } = canvas.getBoundingClientRect()

  // 清空画布
  ctx.value.clearRect(0, 0, width, height)

  // 解析水印文本（支持多行）
  const lines = watermarkText.value.split('\n')
  const lineHeight = watermarkConfig.value.fontSize * 1.4

  // 计算一个水印单元的大小
  const measureText = (text: string) => {
    if (!ctx.value) return { width: 0, height: 0 }
    ctx.value.font = `${watermarkConfig.value.fontWeight} ${watermarkConfig.value.fontSize}px Arial`
    return ctx.value.measureText(text)
  }

  // 计算最大文本宽度
  let maxTextWidth = 0
  lines.forEach(line => {
    const metrics = measureText(line)
    maxTextWidth = Math.max(maxTextWidth, metrics.width)
  })

  const unitWidth = maxTextWidth + 50 // 添加一些边距
  const unitHeight = lines.length * lineHeight + 20

  // 计算需要多少行和列
  const cols = Math.ceil(width / watermarkConfig.value.horizontalSpacing) + 2
  const rows = Math.ceil(height / watermarkConfig.value.verticalSpacing) + 2

  // 设置全局透明度
  ctx.value.globalAlpha = watermarkConfig.value.opacity

  // 循环绘制水印
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const x = col * watermarkConfig.value.horizontalSpacing
      const y = row * watermarkConfig.value.verticalSpacing

      // 创建交错效果（隔行交错）
      const offsetX = row % 2 === 0 ? 0 : watermarkConfig.value.horizontalSpacing / 2
      const offsetY = col % 2 === 0 ? 0 : watermarkConfig.value.verticalSpacing / 2

      drawWatermarkUnit(
        x + offsetX,
        y + offsetY,
        lines,
        lineHeight,
        unitWidth,
        unitHeight
      )
    }
  }

  ctx.value.globalAlpha = 1.0
}

// 绘制单个水印单元
const drawWatermarkUnit = (
  x: number,
  y: number,
  lines: string[],
  lineHeight: number,
  unitWidth: number,
  unitHeight: number
) => {
  if (!ctx.value) return

  // 保存当前画布状态
  ctx.value.save()

  // 移动到水印位置
  ctx.value.translate(x + unitWidth / 2, y + unitHeight / 2)

  // 旋转
  ctx.value.rotate(watermarkConfig.value.rotate * Math.PI / 180)

  // 设置字体样式
  ctx.value.font = `${watermarkConfig.value.fontWeight} ${watermarkConfig.value.fontSize}px Arial`
  ctx.value.fillStyle = watermarkConfig.value.color
  ctx.value.textAlign = 'center'
  ctx.value.textBaseline = 'middle'

  // 绘制多行文本
  lines.forEach((line, index) => {
    const lineY = (index - (lines.length - 1) / 2) * lineHeight
    ctx.value.fillText(line, 0, lineY)
  })

  // 恢复画布状态
  ctx.value.restore()
}

// 更新水印
const updateWatermark = () => {
  if (enabled.value) {
    nextTick(() => {
      initCanvas()
      drawWatermark()
    })
  }
}

// 窗口大小变化时重新绘制
const handleResize = debounce(() => {
  if (enabled.value) {
    updateWatermark()
  }
}, 200)

// 监听状态变化
watch(enabled, (newVal) => {
  if (newVal) {
    updateWatermark()
  } else {
    if (ctx.value && canvasRef.value) {
      const { width, height } = canvasRef.value.getBoundingClientRect()
      ctx.value.clearRect(0, 0, width, height)
    }
  }
})

// 监听水印文字变化
watch(watermarkText, () => {
  if (enabled.value) {
    updateWatermark()
  }
})

// 监听用户名和工作空间名称变化
watch([() => username], () => {
  if (enabled.value) {
    updateWatermark()
  }
})

onMounted(async () => {
  // 登录后获取水印配置
  await fetchWatermarkConfig()

  // 初始绘制
  if (enabled.value) {
    updateWatermark()
  }

  // 监听窗口变化
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
.custom-watermark {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 9999;
  background: transparent;
}
</style>
